/**
 * A seção das imagens candidatas na etapa de análise fotográfica (E87.F2).
 *
 * O que a etapa PROMETE: propor, nunca decidir. As candidatas saem das imagens
 * embutidas nos documentos que o caso já tem (ADR-0079; o teto de três da E87 §E.4
 * caiu na E98.F5), e nenhuma vira foto do caso sem um clique do perito
 * (§2.1: quem elege é ele).
 *
 * A ausência é DECLARADA, e em duas formas diferentes: caso sem documento
 * carregado e caso cujos documentos não trazem imagem aproveitável pedem ações
 * distintas de quem está na tela. Em nenhuma das duas a jornada trava — caso
 * sem foto segue para o exame normalmente.
 *
 * E123.F3 (GUS-128, ADR-0079 E.24): a seção é a seleção de "Trocar fotos", na pré-análise. As
 * imagens repetidas (E123.F1) ficam de fora, a confirmação elege a escolhida no grupo (a escolha do
 * perito substitui a do sistema) e o cartão diz qual candidata está em uso agora.
 */
import { useState } from 'react';

import { Blueprint } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  confirmarCandidataDeImagem,
  excluirCandidataDeImagem,
  lerDocumentoDaCandidata,
  semRepetidas,
  type CandidataDeImagemData,
  type CandidatasDoCasoData,
} from '@/lib/candidatas_de_imagem';
import { ExameEletronicoError } from '@/lib/exame_eletronico';
import type { GrupoDaFoto } from '@/lib/fotos_do_signatario';
import { useCandidatasDoCaso } from '@/lib/useCandidatasDoCaso';

import { CandidataDeImagemCard } from './CandidataDeImagemCard';
import { LeituraEmCurso, LeiturasPendentes } from './AvisosDaLeitura';
import { leituraDaCandidata, LeiturasRetiradas } from './LeituraPropostaDaImagem';

const t = ptBR.analiseFotografica.candidatas;

function mensagemDoErro(err: unknown): string {
  if (err instanceof ExameEletronicoError && err.reason === 'candidata_nao_encontrada') {
    return t.erros.candidata_nao_encontrada;
  }
  if (err instanceof ExameEletronicoError && err.reason === 'candidata_ja_confirmada') {
    return t.erros.candidata_ja_confirmada;
  }
  return t.erros.generico;
}

type GrupoDaLista = 'identidade_frente' | 'retrato' | 'identidade_verso' | 'outras' | 'semClasse';
const ORDEM_DOS_GRUPOS: readonly GrupoDaLista[] = [
  'identidade_frente',
  'retrato',
  'identidade_verso',
  'semClasse',
  'outras',
];

function grupoDaLista(candidata: CandidataDeImagemData): GrupoDaLista {
  if (candidata.classe === undefined || candidata.classe === null) return 'semClasse';
  if (candidata.classe === 'outra') return 'outras';
  return candidata.classe;
}

/**
 * E122.F2 (ADR-0079 E.23): a lista agrupada pela classe lida de cada imagem. Sem nenhuma classe
 * lida (a classificação ainda não rodou, ou não pôde rodar), a lista é a de sempre: uma só.
 * As "outras" — capturas de tela, logotipos, tabelas — ficam recolhidas, com a contagem.
 */
function ListaAgrupada({
  dados,
  cartao,
}: {
  dados: CandidatasDoCasoData;
  cartao: (candidata: CandidataDeImagemData) => JSX.Element;
}): JSX.Element {
  // E123.F1: a mesma imagem em outro tamanho ou documento aparece uma vez só.
  const candidatas = semRepetidas(dados.candidatas);
  const alguma = candidatas.some((c) => c.classe !== undefined && c.classe !== null);
  if (!alguma) {
    return (
      <ul className="space-y-sc-3" data-testid="candidatas-lista">
        {candidatas.map(cartao)}
      </ul>
    );
  }
  return (
    <div className="space-y-sc-4" data-testid="candidatas-lista">
      {ORDEM_DOS_GRUPOS.map((grupo) => {
        const do_grupo = candidatas.filter((c) => grupoDaLista(c) === grupo);
        if (do_grupo.length === 0) return null;
        if (grupo === 'outras') {
          return (
            <details key={grupo} data-testid="candidatas-grupo-outras">
              <summary className="cursor-pointer text-body-sm text-fg-muted underline">
                {t.grupos.outras} — {t.grupos.outrasResumo(do_grupo.length)}
              </summary>
              <ul className="mt-sc-3 space-y-sc-3">{do_grupo.map(cartao)}</ul>
            </details>
          );
        }
        return (
          <section key={grupo} data-testid={`candidatas-grupo-${grupo}`}>
            <h4 className="text-body mb-sc-2 text-fg">{t.grupos[grupo]}</h4>
            <ul className="space-y-sc-3">{do_grupo.map(cartao)}</ul>
          </section>
        );
      })}
    </div>
  );
}

/** As faixas de estado da seção: erro, sucesso, carregando e a classificação em curso (E122.F2). */
function AvisosDaSecao({
  erro,
  sucesso,
  dados,
}: {
  erro: string | null;
  sucesso: string | null;
  dados: CandidatasDoCasoData | null;
}): JSX.Element {
  return (
    <>
      {erro !== null ? (
        <p role="alert" className="text-fg-danger text-body-sm" data-testid="candidatas-erro">
          {erro}
        </p>
      ) : null}
      {sucesso !== null ? (
        <p role="status" className="text-body-sm text-success-700" data-testid="candidatas-sucesso">
          {sucesso}
        </p>
      ) : null}
      {dados === null && erro === null ? (
        <p className="text-body-sm text-fg-muted" data-testid="candidatas-carregando">
          {t.carregando}
        </p>
      ) : null}
      {dados?.classificacao_em_curso === true ? (
        <p
          role="status"
          className="text-body-sm text-fg-muted"
          data-testid="candidatas-classificando"
        >
          {t.classificando}
        </p>
      ) : null}
    </>
  );
}

export interface CandidatasDeImagemProps {
  caseId: string;
  /** O perito concluiu alguma peça: o material do exame não muda mais. */
  concluido: boolean;
  /** Avisa que uma candidata virou foto do caso — a galeria abaixo recarrega. */
  onConfirmada?: () => void;
  /** E123.F3: a candidata em uso agora em cada grupo, pelo código (a eleita de cada grupo). */
  emUso?: Readonly<Record<string, GrupoDaFoto>>;
  /**
   * E123.F3: as leituras propostas das imagens de documento. A pré-análise já as mostra na seção
   * das fotografias; em "Trocar fotos" elas saem daqui para não aparecerem duas vezes.
   */
  comLeituras?: boolean;
  /** A frase de sucesso da confirmação (na troca, a de que a conferência está sendo refeita). */
  mensagemDaConfirmacao?: string;
  /** E191.F1: a troca de um grupo está em voo (`null` ao terminar) — a seção de cima avisa. */
  onTrocando?: ((grupo: GrupoDaFoto | null) => void) | undefined;
}

export function CandidatasDeImagem({
  caseId,
  concluido,
  onConfirmada,
  emUso = {},
  comLeituras = true,
  mensagemDaConfirmacao = t.confirmada,
  onTrocando,
}: CandidatasDeImagemProps): JSX.Element {
  const client = useApiClient();
  // E114.F3.C02 (F-E114.F3-04): relê enquanto a leitura proposta de alguma imagem está em curso.
  const { dados, erro, setErro, emCurso, carregar } = useCandidatasDoCaso(
    client,
    caseId,
    t.erroCarregar,
  );
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [emVoo, setEmVoo] = useState<{
    codigo: string;
    grupo: GrupoDaFoto | 'excluir' | 'ler';
  } | null>(null);

  const confirmar = (candidata: CandidataDeImagemData, grupo: GrupoDaFoto): void => {
    if (emVoo !== null) return;
    void (async () => {
      setEmVoo({ codigo: candidata.codigo, grupo });
      onTrocando?.(grupo);
      setErro(null);
      setSucesso(null);
      try {
        // E123.F2/F3: a escolha do perito vira a eleita do grupo, e a conferência é refeita.
        await confirmarCandidataDeImagem(client, caseId, candidata, grupo, { eleger: true });
        setSucesso(mensagemDaConfirmacao);
        onConfirmada?.();
        await carregar();
      } catch (err) {
        setErro(mensagemDoErro(err));
      } finally {
        setEmVoo(null);
        onTrocando?.(null);
      }
    })();
  };

  // E191.F1 (GT-2026-0030): o perito pede a leitura dos dados da imagem de documento escolhida
  // por ele; o servidor a materializa e lê em segundo plano, e a seção relê até a proposta.
  const ler = (candidata: CandidataDeImagemData): void => {
    if (emVoo !== null) return;
    void (async () => {
      setEmVoo({ codigo: candidata.codigo, grupo: 'ler' });
      setErro(null);
      setSucesso(null);
      try {
        await lerDocumentoDaCandidata(client, caseId, candidata);
        setSucesso(t.leituraPedida);
        onConfirmada?.();
        await carregar();
      } catch (err) {
        setErro(mensagemDoErro(err));
      } finally {
        setEmVoo(null);
      }
    })();
  };

  // E109.F1 (S-08): "não é retrato" — a candidata sai da lista e não volta (a leitura segue).
  const excluir = (candidata: CandidataDeImagemData): void => {
    if (emVoo !== null) return;
    void (async () => {
      setEmVoo({ codigo: candidata.codigo, grupo: 'excluir' });
      setErro(null);
      setSucesso(null);
      try {
        await excluirCandidataDeImagem(client, caseId, candidata);
        setSucesso(t.excluida);
        await carregar();
      } catch (err) {
        setErro(mensagemDoErro(err));
      } finally {
        setEmVoo(null);
      }
    })();
  };

  return (
    <Blueprint
      as="section"
      className="space-y-sc-4 p-sc-6"
      data-testid="candidatas-de-imagem"
      aria-labelledby="candidatas-de-imagem-titulo"
    >
      <div>
        <h3 id="candidatas-de-imagem-titulo" className="text-body-lg text-fg">
          {t.titulo}
        </h3>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.intro}</p>
        {/* E91.F1 (RF06): o critério de extração é ajuda, não instrução. */}
        <details className="mt-1 max-w-prose text-body-sm" data-testid="candidatas-ajuda">
          <summary className="cursor-pointer text-fg-muted underline">{t.ajudaTitulo}</summary>
          <p className="mt-1 text-fg-muted">{t.ajudaTexto}</p>
        </details>
      </div>

      <AvisosDaSecao erro={erro} sucesso={sucesso} dados={dados} />

      {comLeituras ? <LeituraEmCurso emCurso={emCurso} /> : null}
      {comLeituras ? <LeiturasPendentes leituras={dados} /> : null}

      {dados !== null && dados.candidatas.length > 0 ? (
        <ListaAgrupada
          dados={dados}
          cartao={(candidata) => (
            <CandidataDeImagemCard
              key={`${candidata.documento_id}:${candidata.codigo}`}
              caseId={caseId}
              candidata={candidata}
              confirmando={
                emVoo?.codigo === candidata.codigo &&
                (emVoo.grupo === 'documento_identidade' || emVoo.grupo === 'ccb')
                  ? emVoo.grupo
                  : null
              }
              excluindo={emVoo?.codigo === candidata.codigo && emVoo.grupo === 'excluir'}
              onLer={ler}
              lendo={emVoo?.codigo === candidata.codigo && emVoo.grupo === 'ler'}
              bloqueado={concluido || emVoo !== null}
              confirmadaAutomaticamente={
                dados.confirmadas_automaticamente?.includes(candidata.codigo) ?? false
              }
              emUso={emUso[candidata.codigo] ?? null}
              onConfirmar={confirmar}
              onExcluir={excluir}
              onExcluidaDaAnalise={() => {
                setSucesso(ptBR.documentosDeApoio.exclusao.excluidaAgora);
                void carregar();
              }}
              leitura={comLeituras ? leituraDaCandidata(dados, candidata) : undefined}
              onLeituraConfirmada={() => void carregar()}
            />
          )}
        />
      ) : null}

      {comLeituras ? (
        <LeiturasRetiradas
          caseId={caseId}
          leituras={dados}
          concluido={concluido}
          ocupado={emVoo !== null}
          onConfirmada={() => void carregar()}
        />
      ) : null}

      {dados !== null && dados.candidatas.length === 0 ? (
        <p className="max-w-prose text-body-sm text-fg-muted" data-testid="candidatas-ausencia">
          {dados.documentos_varridos === 0 ? t.semDocumento : t.semImagem}
        </p>
      ) : null}
    </Blueprint>
  );
}
